import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Form,
  Icon,
  ICONS,
  Modal,
  Button,
  TextInput,
  Typography,
  DeleteModal,
  SelectInput,
  ColorPickerInput,
  showNotification,
  PLATFORM_THEME_COLORS,
  CheckboxSelect,
} from '@thinknet-gmbh/platform-common-ui';
import { DEFAULT_NAME_FIELD_REGEX, GanttAccessRightsEnum } from '@thinknet-gmbh/platform-common';

import { useGetEmployeesByGantt } from 'src/hooks/employees/get-employees-by-gantt.hook';
import { useGetProjectsByGantt } from 'src/hooks/projects/get-projects-by-gantt.hook';
import {
  COMPANY_GANTT_MODALS,
  DEFAULT_GANTT_TO_DELETE,
} from 'src/pages/settings/components/company/components/company-gantts/company-gantts.constants';
import { useDeleteGanttById } from 'src/hooks/gantts/delete-gantt.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';
import { useGetAllHolidayLocationsByCompany } from 'src/hooks/holiday-manager/get-holiday-locations-by-company.hook';

import { TransferEmployees } from './components/transfer-employees';
import { TransferProjects } from './components/transfer-projects';
import { IGanttFormListProps } from './gantt-form-list.interfaces';
import {
  FormListWrapper,
  DeleteItemContainer,
  GanttInfoWrapper,
  GanttsWrapper,
  GanttColorWrapper,
} from './gantt-form-list.styles';
import { DAYS } from './gantt-form-list.constants';

const GanttFormList: FC<IGanttFormListProps> = ({
  form,
  data,
  fields,
  remove,
  cantDeleteLastElement,
}) => {
  const { t } = useTranslation(['common', 'settings']);
  const [ganttToDelete, setGanttToDelete] = useState(DEFAULT_GANTT_TO_DELETE);
  const [fieldNameToDelete, setFieldNameToDelete] = useState<any>();
  const [activeModal, setActiveModal] = useState<null | string>(null);
  const [isCloseTransferModal, setCloseTransferModal] = useState(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<{ [key: string]: string }>(
    Object.assign({}, ...data.map((d, index) => ({ [index.toString()]: d.holidayLocationId })))
  );

  const currentCompanyId = useGetCurrentCompanyId();

  const { projects, loading: projectsLoading } = useGetProjectsByGantt(ganttToDelete._id);
  const { employees, loading: employeesLoading } = useGetEmployeesByGantt(ganttToDelete._id);
  const { holidayLocations } = useGetAllHolidayLocationsByCompany(currentCompanyId);
  const deleteGantt = useDeleteGanttById({ onSuccess: () => setActiveModal(null) });
  const {
    member: { ganttsAccess },
  } = useGetCurrentMemberAccount();

  const closeModal = () => {
    setActiveModal(null);
    setCloseTransferModal(false);
    setGanttToDelete(DEFAULT_GANTT_TO_DELETE);
  };

  const closeTransferModal = (forceClosing?: boolean) => {
    if (isCloseTransferModal || forceClosing) {
      closeModal();
    }

    setCloseTransferModal(!isCloseTransferModal);
  };

  const onSubmitProjectTransferModal = () => {
    if (employees.length) {
      setActiveModal(COMPANY_GANTT_MODALS.transferEmployees);
    } else {
      setActiveModal(COMPANY_GANTT_MODALS.deleteModal);
    }
  };

  useEffect(() => {
    if (!projectsLoading && !employeesLoading && ganttToDelete._id) {
      if (projects.length) {
        setActiveModal(COMPANY_GANTT_MODALS.transferProjects);
      } else if (employees.length && !projects.length) {
        setActiveModal(COMPANY_GANTT_MODALS.transferEmployees);
      } else {
        setActiveModal(COMPANY_GANTT_MODALS.deleteModal);
      }
    }
  }, [ganttToDelete, projectsLoading, employeesLoading, employees, projects]);

  const onSubmitDeleteModal = () => {
    deleteGantt({ variables: { ganttId: ganttToDelete._id } });
    setActiveModal(null);
    remove(fieldNameToDelete);
    setFieldNameToDelete(null);
    setGanttToDelete(DEFAULT_GANTT_TO_DELETE);
  };

  return (
    <FormListWrapper>
      {activeModal === COMPANY_GANTT_MODALS.deleteModal && (
        <DeleteModal
          title={t('settings:company.deleteModal.title')}
          subTitle={t('settings:company.deleteModal.subtitle', {
            locationName: ganttToDelete.name,
          })}
          onSubmit={onSubmitDeleteModal}
          onClose={closeModal}
          translate={t}
          isOpened={activeModal === COMPANY_GANTT_MODALS.deleteModal}
        />
      )}
      {activeModal === COMPANY_GANTT_MODALS.transferProjects && (
        <Modal
          isOpened={activeModal === COMPANY_GANTT_MODALS.transferProjects}
          onClose={closeTransferModal}
          title={t('settings:company.labels.transferProjects')}
          smallerPadding
        >
          <TransferProjects
            ganttId={ganttToDelete._id}
            projects={projects}
            gantts={data}
            isCloseModalAttempt={isCloseTransferModal}
            closeModalAttempt={closeTransferModal}
            onSubmit={onSubmitProjectTransferModal}
          />
        </Modal>
      )}

      {activeModal === COMPANY_GANTT_MODALS.transferEmployees && (
        <Modal
          isOpened={activeModal === COMPANY_GANTT_MODALS.transferEmployees}
          onClose={closeTransferModal}
          title={t('settings:company.labels.transferEmployees')}
          smallerPadding
        >
          <TransferEmployees
            employees={employees}
            gantts={data}
            ganttId={ganttToDelete._id}
            isCloseModalAttempt={isCloseTransferModal}
            closeModalAttempt={closeTransferModal}
            onSubmit={() => setActiveModal(COMPANY_GANTT_MODALS.deleteModal)}
          />
        </Modal>
      )}
      <GanttsWrapper>
        {fields.map((field: any) => {
          const isContainId = data.find(item => item._id === (field.gantt ? field.gantt._id : ''));
          const isView = ganttsAccess.some(
            (access: any) =>
              access.ganttId === field?.gantt?._id &&
              access.accessRight === GanttAccessRightsEnum.VIEW
          );

          return (
            <div key={field.key}>
              <GanttInfoWrapper isView={isView}>
                <DeleteItemContainer>
                  <Form.Item
                    name={[field.name, 'name']}
                    label={t('settings:company.labels.ganttName')}
                    rules={[
                      {
                        required: true,
                        message: t('settings:company.exceptions.requiredGanttName'),
                      },
                      {
                        pattern: DEFAULT_NAME_FIELD_REGEX,
                        message: t('common:validation.error.incorrectFormat'),
                      },
                    ]}
                  >
                    <TextInput disabled={isView} placeholder="Hamburg" maxLength={100} />
                  </Form.Item>
                  {!isView && (
                    <Icon
                      icon={ICONS.trash}
                      size={22}
                      rightMargin={false}
                      onClick={() => {
                        if (!(fields.length > 1) && cantDeleteLastElement) {
                          showNotification({
                            isIcon: false,
                            message: t('settings:company.notifications.cantPerformAction'),
                            description: t('settings:company.notifications.oneLocationMustExist'),
                            type: 'error',
                          });
                          return;
                        }
                        if (isContainId) {
                          setGanttToDelete({
                            _id: field.gantt._id,
                            name: field.gantt.name,
                          });
                          setFieldNameToDelete(field.name);
                          setCloseTransferModal(false);
                          return;
                        }
                        remove(field.name);
                      }}
                      color={PLATFORM_THEME_COLORS.tone.red}
                    />
                  )}
                </DeleteItemContainer>

                <Form.Item
                  name={[field.name, 'holidayLocationId']}
                  label={t('settings:holidayManager.labels.holidayLocation')}
                  rules={[
                    {
                      required: true,
                      message: t('settings:company.exceptions.requiredGanttName'),
                    },
                  ]}
                >
                  <SelectInput
                    onChange={value =>
                      setSelectedLocationIds({ ...selectedLocationIds, [field.key]: value })
                    }
                    disabled={isView}
                    placeholder={t('common:placeholders.selectFromList')}
                    defaultValue={data[field.key]?.holidaySubGroup?.holidayLocationId}
                  >
                    {holidayLocations.map((holidayLocation: any) => (
                      <SelectInput.Option key={holidayLocation._id} value={holidayLocation._id}>
                        {holidayLocation.name}
                      </SelectInput.Option>
                    ))}
                  </SelectInput>
                </Form.Item>
              </GanttInfoWrapper>

              <Form.Item
                label={t('gantt:labels.weekends')}
                name={[field.name, 'weekends']}
                initialValue={['saturday', 'sunday']}
                rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
              >
                <CheckboxSelect
                  showSearch
                  placeholder={t('gantt:labels.selectNeededWeekends')}
                  optionData={DAYS.map((value: string) => ({
                    value,
                    label: t(`common:days.${value}`),
                  }))}
                />
              </Form.Item>
              <Typography fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
                {t('settings:company.labels.daysColor')}
              </Typography>

              <GanttColorWrapper>
                <Form.Item
                  name={[field.name, 'colorSchema', 'holidayColor']}
                  label={t('settings:company.labels.colorSchema.holiday')}
                  initialValue={PLATFORM_THEME_COLORS.tone.markYellow}
                  rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
                >
                  <ColorPickerInput disabled={isView} />
                </Form.Item>
                <Form.Item
                  name={[field.name, 'colorSchema', 'todayColor']}
                  label={t('settings:company.labels.colorSchema.today')}
                  initialValue={PLATFORM_THEME_COLORS.tone.twilightBlue}
                  rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
                >
                  <ColorPickerInput disabled={isView} />
                </Form.Item>
                <Form.Item
                  name={[field.name, 'colorSchema', 'weekendColor']}
                  label={t('settings:company.labels.colorSchema.weekend')}
                  initialValue={PLATFORM_THEME_COLORS.grayVariations.athensGray}
                  rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
                >
                  <ColorPickerInput disabled={isView} />
                </Form.Item>
              </GanttColorWrapper>

              <Button
                type="text"
                onClick={() => {
                  const { gantts } = form.getFieldsValue('gantts');

                  const targetGantt = { ...gantts[field.name] };

                  targetGantt.colorSchema = {
                    holidayColor: PLATFORM_THEME_COLORS.tone.markYellow,
                    todayColor: PLATFORM_THEME_COLORS.tone.twilightBlue,
                    weekendColor: PLATFORM_THEME_COLORS.grayVariations.athensGray,
                  };

                  gantts[field.name] = targetGantt;

                  form.setFieldsValue({ gantts });
                }}
                icon={ICONS.brush}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
              >
                {t('settings:company.labels.colorSchema.resetToDefault')}
              </Button>
            </div>
          );
        })}
      </GanttsWrapper>
    </FormListWrapper>
  );
};

export default GanttFormList;
